/**
 * The one route guard (Deep-test ADM-006).
 *
 * App.tsx's SuspenseWrapper puts every route's element through this. It looks
 * the current path up in config/routeAccess.ts — the same map the sidebar
 * reads — and shows <NoAccess /> instead of the page when the signed-in user
 * cannot open it. The page is never mounted, so it sends nothing and cannot
 * render an empty state built from refusals.
 *
 * A public route passes untouched. A user not yet hydrated (signed in, profile
 * not loaded) gets the loading spinner rather than a "no access" flash.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { canOpenRoute, routeFor } from '@/config/routeAccess';
import LoadingSpinner from '@/components/Common/LoadingSpinner';
import NoAccess from '@/components/Common/NoAccess';
import { hasNoSite, noSitePath, openWithNoSite } from '@/config/techExperience';

export default function RouteGate({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const route = routeFor(pathname);
  // No declared route: the catch-all redirect handles it, and the ADM-006
  // guard test keeps every real route declared.
  if (!route || route.access === 'public') return <>{children}</>;
  if (!user) return <LoadingSpinner fullPage label="Loading..." />;
  // FM Technician C2: a technician with no site yet sees "No site yet — ask
  // your FM" instead of job screens that would answer nothing; their own
  // self-service (desk, visitors, requests, profile, safety) stays open.
  if (hasNoSite(user) && !openWithNoSite(pathname)) return <Navigate to={noSitePath(user)} replace />;
  if (!canOpenRoute(user, route.pattern)) return <NoAccess />;
  return <>{children}</>;
}
